import React, { useLayoutEffect, useRef, useEffect, useState, useMemo } from 'react';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import * as am5core from '@amcharts/amcharts5';
import am5themes_Dark from '@amcharts/amcharts5/themes/Dark';
import './AnalysisChart.scss';
import FullScreenIcon from '../../features/common/FullScreenIcon/FullScreenIcon';
import { RootState } from '../../store';
import { useSelector } from 'react-redux';
import { exportChart } from '../../utilities/ExportChart';
import DownloadChart from '../time-series-chart/download/downloadChart';
import EmptyState from '../empty-state/EmptyState';
import { useAppSelector } from '../../hooks/storeHooks';
import {
  createChartLegend,
  createChartTooltip,
  createCursorBullet,
  customizeAxisLabels,
  setupCursor,
  setupCursorEvents,
  updateYAxisRange,
} from '../../utilities/chartHelpers';
import Loader from '../../features/common/Loader/Loader';
import { AnalysisChartData } from '../../services/dashboard/AnalysisChartService';
import { showWarningToast } from '../toast/toastBridge';
import { ProtectAction } from '../../rbac';

interface AnalysisChartProps {
  analysisChartPropsData: any;
  rPermission: { [key: string]: boolean; }
}

const AnalysisChart: React.FC<AnalysisChartProps> = ({ analysisChartPropsData, rPermission }) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [enableStep, setEnableStep] = useState(false);
  const seriesVisibilityRef = useRef<Record<string, boolean>>({});
  const startDate = useSelector((state: RootState) => state.AssetTrendDataSlice.startDate);
  const endDate = useSelector((state: RootState) => state.AssetTrendDataSlice.endDate);
  const analysisChartData = useAppSelector((state: RootState) => state.AnalysisChartDataSlice.data) as
    | AnalysisChartData
    | undefined;
  const timeSeriesChartData = useAppSelector((state: any) => state.AssetTrendDataByTagsSlice.data);
  const chartViewOptions = useAppSelector((state: any) => state.AssetTrendDataSlice.chartViewOptions);
  const wellTestDataLoading = useSelector((state: RootState) => state.WellTestChartDataSlice.loading);
  const timeSeriesChartDataLoading = useAppSelector((state: any) => state.AssetTrendDataByTagsSlice.loading);

  const parameterData = analysisChartPropsData?.widgetProperty.widgetProperties.parameterConfig[1]?.parameters;
  const { showTooltip } = chartViewOptions;

  const dynamicSeriesConfig = useMemo(() => {
    if (!parameterData || !Array.isArray(parameterData)) {
      return [];
    }

    const sortedParams = [...parameterData].sort(
      (a, b) => (parseInt(a.displayOrder) || 0) - (parseInt(b.displayOrder) || 0),
    );

    return sortedParams.map((param: any, index: number) => {
      const fieldName = param.name.replace(/\s+/g, '').replace(/[()%]/g, '').replace(/\//g, '');

      return {
        name: param.name,
        field: fieldName,
        color: param.lineColor || '#1DB7DF',
        visible:
          seriesVisibilityRef.current[fieldName] !== undefined ? seriesVisibilityRef.current[fieldName] : index === 0,
        tooltipLabel: param.description || param.name,
        units: param.units || '',
        pst: param.pst || null,
        displayOrder: parseInt(param.displayOrder) || index,
      };
    });
  }, [parameterData, enableStep]);

  const { seriesKeyMap, timeSeriesFieldMap, paramTypeIdToField } = useMemo(() => {
    const seriesMap: Record<string, string> = {};
    const timeSeriesMap: Record<string, string> = {};
    const paramTypeMap: Record<string, string> = {};

    if (parameterData && Array.isArray(parameterData)) {
      parameterData.forEach((param: any) => {
        const fieldName = param.name.replace(/\s+/g, '').replace(/[()%]/g, '').replace(/\//g, '');

        if (param.pst && param.pst !== '') {
          // Has pst - for time series data
          timeSeriesMap[param.name] = fieldName;
          paramTypeMap[param.pst] = param.name;
        } else {
          // No pst - for analysis data
          seriesMap[param.description || param.name] = fieldName;
        }
      });
    }

    return {
      seriesKeyMap: seriesMap,
      timeSeriesFieldMap: timeSeriesMap,
      paramTypeIdToField: paramTypeMap,
    };
  }, [parameterData]);

  const chartData = useMemo(() => {
    const processAnalysisData = () => {
      if (!analysisChartData?.Series || Object.keys(seriesKeyMap).length === 0) return [];

      const allTimestamps = new Set<number>();
      Object.values(analysisChartData.Series).forEach((series: any) => {
        if (Array.isArray(series.Data)) {
          series.Data.forEach((point: any) => allTimestamps.add(point.Timestamp));
        }
      });

      const remappedSeries: Record<string, any> = {};
      Object.entries(seriesKeyMap).forEach(([displayName, fieldName]) => {
        if (analysisChartData.Series[displayName]) {
          remappedSeries[fieldName] = analysisChartData.Series[displayName];
        }
      });

      return Array.from(allTimestamps)
        .sort((a, b) => a - b)
        .map((timestamp) => {
          const obj: any = { Timestamp: timestamp };
          Object.values(seriesKeyMap).forEach((fieldName) => {
            const series = remappedSeries[fieldName];
            if (series?.Data) {
              const point = series.Data.find((p: any) => p.Timestamp === timestamp);
              obj[fieldName] = point?.Value || null;
            }
          });
          return obj;
        });
    };

    const processTimeSeriesData = () => {
      if (!Array.isArray(timeSeriesChartData) || Object.keys(timeSeriesFieldMap).length === 0) return [];

      const grouped: Record<number, any> = {};
      timeSeriesChartData.forEach((item: any) => {
        const timestamp = new Date(item.date).getTime();
        if (!grouped[timestamp]) grouped[timestamp] = { Timestamp: timestamp };

        const fieldKey =
          (item.paramTypeId && paramTypeIdToField[item.paramTypeId]) ||
          Object.keys(timeSeriesFieldMap).find(
            (key) => key.replace(/\s/g, '').toLowerCase() === item.name.replace(/\s/g, '').toLowerCase(),
          );

        if (fieldKey) {
          const mappedField =
            timeSeriesFieldMap[fieldKey] || fieldKey.replace(/\s+/g, '').replace(/[()%]/g, '').replace(/\//g, '');
          const numericValue = Number(item.value);
          if (!isNaN(numericValue) && numericValue !== 0) {
            grouped[timestamp][mappedField] = numericValue;
          }
        }
      });
      return Object.values(grouped);
    };

    const mergeData = (timeSeriesArray: any[], analysisArray: any[]) => {
      const mergedByTimestamp: Record<number, any> = {};
      [...timeSeriesArray, ...analysisArray].forEach((item) => {
        const ts = item.Timestamp;
        mergedByTimestamp[ts] = mergedByTimestamp[ts] ? { ...mergedByTimestamp[ts], ...item } : { ...item };
      });
      return Object.values(mergedByTimestamp).sort((a: any, b: any) => a.Timestamp - b.Timestamp);
    };

    const fillMissingValues = (data: any[]) => {
      const fields = [...Object.values(seriesKeyMap), ...Object.values(timeSeriesFieldMap)];
      const lastKnown: Record<string, any> = {};

      data.forEach((row) => {
        fields.forEach((field) => {
          if (row[field] !== undefined && row[field] !== null) {
            lastKnown[field] = row[field];
          } else if (lastKnown[field] !== undefined) {
            row[field] = lastKnown[field];
          }
        });
      });
      return data;
    };

    const filterByDateRange = (data: any[]) => {
      const startMs = startDate ? new Date(startDate).getTime() : undefined;
      const endMs = endDate ? new Date(endDate).getTime() : undefined;

      return data.filter((item: any) => {
        const ts = item.Timestamp;
        return (!startMs || ts >= startMs) && (!endMs || ts <= endMs);
      });
    };

    const analysisArray = processAnalysisData();
    const timeSeriesArray = processTimeSeriesData();
    const mergedData = mergeData(timeSeriesArray, analysisArray);
    const filledData = fillMissingValues(mergedData);
    return filterByDateRange(filledData);
  }, [analysisChartData, timeSeriesChartData, startDate, endDate, parameterData]);

  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const toggleFullscreen = () => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().catch(console.error);
    } else {
      document.exitFullscreen();
    }
  };

  const handleChartDownload = async (option: string) => {
    const element = document.getElementById('analysis-chart-content');
    if (!element) return;
    const exportType = option === 'Download as PDF' ? 'PDF' : 'JPG';
    await exportChart(element, exportType, 'AnalysisChart', undefined, undefined, 'Analysis Chart');
  };

  const createTooltipFunction = (chart: am5xy.XYChart, root: am5.Root) => {
    if (!showTooltip) return undefined;

    return createChartTooltip(chart, root, dynamicSeriesConfig, false);
  };

  const createSeries = (
    config: any,
    chart: am5xy.XYChart,
    xAxis: am5xy.DateAxis<am5xy.AxisRenderer>,
    yAxis: am5xy.ValueAxis<am5xy.AxisRenderer>,
    root: am5.Root,
  ) => {
    const commonConfig = {
      name: config.name,
      xAxis,
      yAxis,
      valueYField: config.field,
      valueXField: 'Timestamp',
      stroke: am5.color(config.color),
      fill: am5.color(config.color),
      tooltip: createTooltipFunction(chart, root),
      visible: config.visible,
    };

    const series = enableStep
      ? chart.series.push(am5xy.StepLineSeries.new(root, { ...commonConfig, noRisers: false }))
      : chart.series.push(am5xy.LineSeries.new(root, { ...commonConfig, connect: true }));

    series.strokes.template.setAll({ strokeWidth: 2 });
    return series;
  };

  useLayoutEffect(() => {
    if (!chartRef.current || chartData.length === 0 || dynamicSeriesConfig.length === 0) return;

    const root = am5.Root.new(chartRef.current);
    root.setThemes([am5themes_Dark.new(root)]);

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        layout: root.verticalLayout,
        paddingLeft: 0,
        paddingRight: 30,
        paddingTop: 20,
        paddingBottom: 0,
        background: am5.Rectangle.new(root, {
          fill: am5.color('#122031'),
          fillOpacity: 0,
        }),
      }),
    );

    const xAxis = chart.xAxes.push(
      am5xy.DateAxis.new(root, {
        maxDeviation: 0.2,
        baseInterval: { timeUnit: 'minute', count: 1 },
        renderer: am5xy.AxisRendererX.new(root, {
          strokeWidth: 1,
          strokeOpacity: 1,
          stroke: am5core.color('#4A5463'),
          minGridDistance: 80,
          pan: 'zoom',
        }),
      }),
    );

    if (startDate && endDate) {
      xAxis.setAll({
        min: new Date(startDate).getTime(),
        max: new Date(endDate).getTime(),
        strictMinMax: true,
      });
    }

    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {
          pan: 'zoom',
          strokeWidth: 1,
          strokeOpacity: 1,
          stroke: am5core.color('#4A5463'),
        }),
      }),
    );

    yAxis.children.unshift(
      am5.Label.new(root, {
        rotation: -90,
        text: 'Analysis',
        y: am5.percent(50),
        centerX: am5.percent(50),
        textAlign: 'center',
        oversizedBehavior: 'wrap',
        maxHeight: 500,
        fontFamily: 'Mulish',
        fontSize: 12,
        fontWeight: '400',
        marginRight: -5,
        minWidth: 45,
      }),
    );

    customizeAxisLabels(yAxis.get('renderer'), 14);

    const allSeries: (am5xy.LineSeries | am5xy.StepLineSeries)[] = [];
    const bullets: am5.Bullet[] = [];
    const sortedConfig = [...dynamicSeriesConfig].sort((a, b) => a.displayOrder - b.displayOrder);

    sortedConfig.forEach((config) => {
      const series = createSeries(config, chart, xAxis, yAxis, root);
      allSeries.push(series);
      const bullet = createCursorBullet(root, config.color);
      bullets.push(bullet);
      series.bulletsContainer.children.push(bullet.get('sprite'));
    });

    const cursor = setupCursor(chart, root, xAxis, '#60BFDA', 2, yAxis);

    const chartDataForCursor = sortedConfig.map((config) => ({
      title: config.tooltipLabel,
      shortUnitOfMeasure: config.units,
    }));

    const parameterDataForCursor = sortedConfig.map((config) => ({
      name: config.tooltipLabel,
      lineColor: config.color,
      markerColor: config.color,
    }));

    setupCursorEvents(cursor, xAxis, chart, allSeries, bullets, chartDataForCursor, parameterDataForCursor, 'line');
    allSeries.forEach((series) => series.data.setAll(chartData));

    const legend = createChartLegend(chart, root);
    legend.data.setAll(chart.series.values);

    legend.dataItems.forEach((dataItem, index) => {
      const series = dataItem.dataContext as am5xy.LineSeries | am5xy.StepLineSeries;
      const config = sortedConfig[index];
      const hasData = series?.data?.values?.some((item: any) => {
        const value = item?.[config?.field];
        return value != null && !isNaN(Number(value));
      });
      if (hasData) {
        (series as any).setPrivate('legendMarkerFill', am5.color(config.color));
      }
    });

    legend.itemContainers.each((itemContainer) => {
      itemContainer.events.on('click', () => {
        setTimeout(() => {
          updateYAxisRange(allSeries, yAxis);

          allSeries.forEach((series, index) => {
            const config = sortedConfig[index];
            seriesVisibilityRef.current[config.field] = (series as any).get('visible') !== false;
          });

          const visibleSeries = allSeries.filter((series) => {
            return (series as any).get('visible') !== false;
          });
          cursor.set('snapToSeries', visibleSeries);
        }, 0);
      });
    });

    updateYAxisRange(allSeries, yAxis);
    chart.appear(1000, 100);

    return () => root.dispose();
  }, [chartData, chartViewOptions, showTooltip, startDate, endDate, dynamicSeriesConfig, enableStep]);

  const handleStepToggle = () => {
    if (!rPermission.canEnableStep) {
      showWarningToast('You do not have permission to enable/disable step option for Analysis graphs chart. Please contact your administrator.');
      return;
    }
    setEnableStep(!enableStep);
  };

  const hasPlottableData = useMemo(() => {
    if (chartData.length === 0 || dynamicSeriesConfig.length === 0) return false;

    return chartData.some((item) =>
      dynamicSeriesConfig.some(
        (config) =>
          item[config.field] !== null && item[config.field] !== undefined && !isNaN(Number(item[config.field])),
      ),
    );
  }, [chartData, dynamicSeriesConfig]);

  return (
    <section
      ref={containerRef}
      className={`ogl-chart-container ${isFullscreen ? 'fullscreen' : ''}`}
      style={!isFullscreen ? { background: 'transparent' } : undefined}
    >
      {wellTestDataLoading || timeSeriesChartDataLoading ? (
        <Loader />
      ) : (
        <>
          <header className='ogl-chart-header'>
            <div className='chart-title'>
              <h2>Analysis graphs</h2>
            </div>
            <div className='chart-actions'>
              <ProtectAction canAccess={rPermission.canAnalysisGraphExpand}>
                <button className='chart-action-button' onClick={toggleFullscreen}>
                  <FullScreenIcon />
                </button>
              </ProtectAction>
              <button className='chart-action-button'>
                <DownloadChart
                  onClick={handleChartDownload}
                  canExportPdf={rPermission.canAnalysisGraphExportPdf}
                  canExportJpg={rPermission.canAnalysisGraphExportJpg}
                />
              </button>
            </div>
          </header>

          {isFullscreen && <div className='chart-divider'></div>}

          {hasPlottableData ? (
            <>
              <div id='analysis-chart-content'>
                <div
                  ref={chartRef}
                  className='analysis-chart-container'
                  style={{ height: isFullscreen ? '100%' : '120%' }}
                ></div>
              </div>
              <ProtectAction canAccess={rPermission.canEnableStep}>
                <div className='analysis-chart-controls'>
                  <div className='step-checkbox-container'>
                    <div className={`checkbox ${enableStep ? 'enabled' : ''}`} onClick={handleStepToggle}>
                      {enableStep && <div className='checkbox-icon' />}
                    </div>
                  </div>
                  Enable step
                </div>
              </ProtectAction>
            </>
          ) : (
            <EmptyState message='No data available for the selected date range. Please adjust your selection and try again' />
          )}
        </>
      )}
    </section>
  );
};

export default AnalysisChart;
